/*
 * A commander's registered designs (the state's `designs`), and which have
 * been retired since: a retired design leaves the list, its units keep
 * serving, and barracks can't be set to it (one already making it carries
 * on until changed).
 */

import { createSignal } from "solid-js";

/** Every design id seen registered, and those gone from the list since. */
const seen = new Set<number>();
const [retired, setRetired] = createSignal<Set<number>>(new Set());
export { retired };

/** Whether a design may be offered to barracks: not retired this session
 *  (the server marks retired ones unproducible, build_pct 0, which callers
 *  already filter on; names repeat, so never pick by name). */
export function offerable(id: number, _name: string): boolean {
  return !retired().has(id);
}

/** Each of the commander's designs' drive ("Combustion", "Electric"), from
 *  its spec: a titan's upkeep depends on it. */
const powers = new Map<number, string>();
export const powerOf = (id: number): string | undefined => powers.get(id);
/** The commander's titan designs standing unpaid this minute (the state's
 *  upkeep): their units stand and hold fire until the next payment. */
let unpowered = new Set<number>();
export const isUnpowered = (design: number): boolean => unpowered.has(design);

export function setDesigns(state: Record<string, unknown>): void {
  const list = state.designs as { id: number; name?: string; spec?: { power?: string } }[] | undefined;
  if (!list) return;
  for (const d of list) if (d.spec?.power) powers.set(d.id, d.spec.power);
  unpowered = new Set(((state.upkeep as { unpowered_designs?: number[] } | undefined)?.unpowered_designs ?? []));
  const now = new Set(list.map((d) => d.id));
  for (const id of now) seen.add(id);
  const gone = [...seen].filter((id) => !now.has(id));
  if (gone.length !== retired().size || gone.some((id) => !retired().has(id))) setRetired(new Set(gone));
}

/** Marks a design retired at once (the state confirms it). */
export function markRetired(id: number): void {
  setRetired((r) => new Set([...r, id]));
}
